/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable no-console */
import { config } from '@brands/config/config';
import { useLocalStorageState } from '@brands/hooks';
import { useAppDispatch } from '@brands/hooks/useReduxHook';
import { LoginResponse } from '@brands/services/auth/login';
import { getBrowserLocationState } from '@brands/services/geolocation/getBrowserLocationState';
import { getCurrentOrganization } from '@brands/services/identity/getCurrentOrganization';
import { getCurrentProfile } from '@brands/services/identity/getCurrentProfile';
import { getPricings } from '@brands/services/identity/getPricings';
import { UserProfile, UserRoleName } from '@brands/services/identity/types/UserProfile';
import { getZendeskUrl } from '@brands/services/zendesk/getZendeskUrl';
import { setUser } from '@brands/store/slices/authSlice';
import { setPatientState } from '@brands/store/slices/currentPatientState';
import { setShowStateModal } from '@brands/store/slices/location';
import { setOrganizationPrices } from '@brands/store/slices/organizationPricingSlice';
import { setOrganization } from '@brands/store/slices/organizationSlice';
import { displayErrorDetails } from '@brands/Utils/displayError';
import { cookieOptions } from '@brands/Utils/getToken';
import { getCurrentPState, setCurrentPatientState } from '@brands/Utils/localStorage';
import Cookies from 'js-cookie';
import moment from 'moment';
import { useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { INVITE_TOKEN_KEY } from '../Auth/assets/helpers';
import { acceptInviteByToken } from '../services/patient_plan/services';

type AuthJWT = { isAuthenticated: boolean };

export type InviteModalPayload = {
  status: 'JOINED' | 'HAS_PLAN' | 'INVALID_TOKEN' | 'AT_CAPACITY' | 'OWNER_CANNOT_JOIN' | 'UNKNOWN_ERROR';
};

// Upper bound for the pre-redirect, best-effort patient-location capture. The
// browser geolocation lookup already caps at 10s, but the follow-up
// reverse-geocode request rides the shared API client, which has no per-request
// timeout — so bound the whole step to keep a stalled network from hanging the
// organization redirect.
export const LOCATION_HANDOFF_TIMEOUT_MS = 12000;

export async function syncPatientLocation(
  user: UserProfile,
  dispatch: ReturnType<typeof useAppDispatch>,
): Promise<void> {
  if (user.role.name !== UserRoleName.Patient) return;

  const storedLocationState = getCurrentPState(user.id);

  const applyState = (stateValue: string, persist = false): void => {
    dispatch(setPatientState({ userId: user.id, state: stateValue }));
    if (persist) {
      setCurrentPatientState(user.id, stateValue);
    }
    dispatch(setShowStateModal({ showStateModal: stateValue !== storedLocationState }));
  };

  const useStoredStateOrPrompt = (): void => {
    if (storedLocationState) {
      applyState(storedLocationState);
      return;
    }

    // No US location and nothing stored: don't guess a state into
    // Redux/localStorage as if it were confirmed — just open the modal (it
    // pre-fills a suggested default for display) and let the patient's
    // explicit choice be the first real write.
    dispatch(setShowStateModal({ showStateModal: true }));
  };

  const browserLocationState = await getBrowserLocationState();

  // If the patient picked a state while detection was in flight, their explicit
  // choice wins over anything resolved here — the detected (possibly coarse)
  // location or the stale pre-lookup fallback.
  if (getCurrentPState(user.id) !== storedLocationState) return;

  if (browserLocationState) {
    applyState(browserLocationState, true);
    return;
  }

  useStoredStateOrPrompt();
}

export function usePostAuth(): {
  triggerPostAuth: (
    response: LoginResponse,
    opts?: {
      event?: 'login' | 'sign_up';
      preloadPricingForPatient?: boolean;
      onInviteResult?: (payload: InviteModalPayload) => void;
      suspendNavigationIfInvite?: boolean;
    },
  ) => Promise<void>;
} {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [, setAuthJWT] = useLocalStorageState<AuthJWT>('isAuthenticated', { isAuthenticated: false });

  const triggerPostAuth = useCallback(
    async (
      response: LoginResponse,
      opts?: {
        event?: 'login' | 'sign_up';
        preloadPricingForPatient?: boolean;
        onInviteResult?: (payload: InviteModalPayload) => void;
        suspendNavigationIfInvite?: boolean;
      },
    ) => {
      try {
        const setCookieSafe = (k: string, v: string): void => {
          try {
            Cookies.set(k, v, cookieOptions());
          } catch {
            /* noop */
          }
        };

        // 1) Persist tokens
        const { access_token, refresh_token, role, redirect_url } = response;
        if (access_token) setCookieSafe('jwtToken', access_token as string);
        if (refresh_token) setCookieSafe('refreshToken', refresh_token as string);
        setCookieSafe('jwtTokenExpire', moment().add(20, 'hours').valueOf().toString());

        // 2) Optional org redirect (Dentistry.One)
        if (redirect_url && typeof window !== 'undefined' && config.organization_id === 'dentistry-one') {
          // Best-effort: capture and persist the patient's browser location before
          // handing off, so a same-origin destination can read it (from
          // localStorage) and skip both the re-prompt and the New York fallback.
          // This MUST NOT block the redirect: the reverse-geocode call has no
          // client-side timeout, so bound the whole step and swallow any
          // profile/geolocation failure — reaching the org app is what matters.
          try {
            await Promise.race([
              (async (): Promise<void> => {
                const patient = await getCurrentProfile(role);
                await syncPatientLocation(patient, dispatch);
              })(),
              new Promise<void>((resolve) => {
                setTimeout(resolve, LOCATION_HANDOFF_TIMEOUT_MS);
              }),
            ]);
          } catch {
            /* location capture is best effort; never block the redirect */
          }
          window.location.href = redirect_url;
          return;
        }

        // 3) Profile
        const user = await getCurrentProfile(role);
        dispatch(setUser({ userInfo: user }));

        // 4) Zendesk SSO
        const returnTo = searchParams.get('return_to');
        if (returnTo) {
          try {
            const { url } = await getZendeskUrl(returnTo);
            window.location.href = url;
            return;
          } catch {
            // keep user in app if not configured
          }
        }

        // 5) Pending invite (patients only)
        const handlePendingInvite = async (userRole: string): Promise<boolean> => {
          let suspend = false;
          const pendingInvite = localStorage.getItem(INVITE_TOKEN_KEY);
          if (pendingInvite && userRole === 'Patient') {
            try {
              const res = await acceptInviteByToken(pendingInvite);
              const payload = { status: res.status } as InviteModalPayload;
              opts?.onInviteResult?.(payload);
              if (opts?.suspendNavigationIfInvite && payload.status !== 'JOINED') {
                suspend = true;
                sessionStorage.setItem('authSuspend', '1');
              }
            } catch {
              const payload = { status: 'UNKNOWN_ERROR' } as InviteModalPayload;
              opts?.onInviteResult?.(payload);
              if (opts?.suspendNavigationIfInvite) {
                suspend = true;
                sessionStorage.setItem('authSuspend', '1');
              }
            } finally {
              localStorage.removeItem(INVITE_TOKEN_KEY);
            }
          } else {
            sessionStorage.removeItem('authSuspend');
          }
          return suspend;
        };

        const suspendNavigation = await handlePendingInvite(user.role.name);

        // 7) Org hydration for non-admin/non-patient
        if (
          user.role.name !== UserRoleName.Admin &&
          user.role.name !== UserRoleName.SuperAdmin &&
          user.role.name !== UserRoleName.OrganizationAdmin &&
          user.role.name !== UserRoleName.Patient
        ) {
          const org = await getCurrentOrganization();
          dispatch(setOrganization({ currentOrganization: org }));
        }

        // 8) Optional price preload
        if (opts?.preloadPricingForPatient && user.role.name === UserRoleName.Patient) {
          try {
            const prices = await getPricings();
            dispatch(setOrganizationPrices({ currentOrganizationPrices: prices }));
          } catch (e) {
            console.warn('Failed to preload pricing:', e);
          }
        }

        // 9) Analytics
        (window as any).dataLayer = (window as any).dataLayer || [];
        (window as any).dataLayer.push({
          event: opts?.event ?? 'login',
          userId: user.id,
        });

        if (!suspendNavigation) {
          setAuthJWT({ isAuthenticated: true });
        }

        // 10) Navigate (or suspend)
        // Start location detection before the suspend check: a patient whose
        // navigation is held for the invite modal still lands in the app
        // afterwards, and skipping the lookup entirely left them with no
        // location at all for the rest of the session. It stays unawaited —
        // detection can take tens of seconds behind a permission prompt, and
        // nothing here should block on it.
        void syncPatientLocation(user, dispatch);

        if (suspendNavigation) return;

        const params = new URLSearchParams(window.location.search);
        navigate(params.get('redirect_uri') || `/${user.role.name}/dashboard`);
      } catch (error) {
        displayErrorDetails(error);
        throw error;
      }
    },
    [dispatch, navigate, searchParams, setAuthJWT],
  );

  return { triggerPostAuth };
}
